import {
  Text,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from '@fluentui/react-components';
import { Skeleton, SkeletonItem } from '@fluentui/react-components';
import spaceRoomPlaceholder from '@/assets/images/space-card-room-placeholder.png';
import { SpaceImageFallback } from '@components/spaces/SpaceImageFallback';
import { getRoomLayoutMeta } from '@components/spaces/SpaceCard/SpaceCard.constants';
import { useAuthenticatedImage } from '@/hooks/useAuthenticatedImage';
import type { EventSpaceSelection } from '@/store/models/events';
import type { SpaceWithAvailability } from '@/store/models/spaces';
import type { Building } from '@/store/models/buildings';
import { useSelectedSpacesAccordionStyles } from './SelectedSpacesAccordion.styles';
import { getRoomSetupCapacity, getRoomSetupCount } from './roomSetupCapacity';

/** Minimal translate signature (namespace-bound `t` from `useTranslation`). */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** A selected space paired with the resolved catalog details for its building. */
export interface ResolvedSelectedSpace {
  selection: EventSpaceSelection;
  details?: SpaceWithAvailability;
}

interface SpaceItemProps {
  index: number;
  space: ResolvedSelectedSpace;
  building: Building | undefined;
  fallbackCapacity?: number;
  t: TranslateFn;
}

const buildConfiguration = (
  codes: string[] | undefined,
  details: SpaceWithAvailability | undefined
): string | undefined =>
  codes && codes.length > 0
    ? codes
        .map((code) => {
          const title = getRoomLayoutMeta(code).title;
          const count = getRoomSetupCount(code, details);
          return count != null ? `${title} (${count})` : title;
        })
        .join(', ')
    : undefined;

const buildAddress = (
  building: Building | undefined,
  details: SpaceWithAvailability | undefined,
  spaceName: string | undefined
): string[] => {
  const parts: string[] = [];
  if (building?.buildingName) parts.push(building.buildingName);
  if (details?.floor) parts.push(`Floor ${details.floor}`);
  if (spaceName) parts.push(spaceName);
  if (building?.buildingAddress) parts.push(building.buildingAddress);
  return parts;
};

const SpaceItem = ({ index, space, building, fallbackCapacity, t }: SpaceItemProps) => {
  const styles = useSelectedSpacesAccordionStyles();
  const { details, selection } = space;
  const spaceName = details?.spaceName ?? selection.spaceName ?? t('notProvided');
  const capacity = getRoomSetupCapacity(selection, details, fallbackCapacity);
  const configuration = buildConfiguration(selection.selectedSetupCodes, details);
  const addressParts = buildAddress(building, details, details?.spaceName ?? selection.spaceName);
  const address = addressParts.length > 0 ? addressParts.join(', ') : t('notProvided');

  const {
    src: imageSrc,
    isLoading: isImageLoading,
    isError: isImageError,
  } = useAuthenticatedImage(details?.imageUrl, spaceRoomPlaceholder);
  const showFallback = !details?.imageUrl || isImageError;

  const summaryParts = [
    [building?.buildingName, details?.floor ? `Floor ${details.floor}` : undefined]
      .filter(Boolean)
      .join(', ') || undefined,
    configuration,
    capacity != null ? `${t('fields.capacity')} ${capacity}` : undefined,
  ].filter(Boolean) as string[];

  return (
    <AccordionItem
      value={index}
      className={styles.item}
      data-testid={`selected-space-item-${index}`}
    >
      <AccordionHeader expandIconPosition="end" className={styles.header}>
        <div className={styles.headerContent}>
          <span className={styles.indexBadge} aria-hidden="true">
            {index + 1}
          </span>
          <span className={styles.headerText}>
            <Text className={styles.headerTitle}>{spaceName}</Text>
            {summaryParts.length > 0 && (
              <Text className={styles.headerSummary}>{summaryParts.join(' \u2022 ')}</Text>
            )}
          </span>
        </div>
      </AccordionHeader>
      <AccordionPanel className={styles.accordionPanel}>
        <div className={styles.panel}>
          {isImageLoading ? (
            <Skeleton aria-label="Loading image">
              <SkeletonItem className={styles.image} />
            </Skeleton>
          ) : showFallback ? (
            <SpaceImageFallback className={styles.image} />
          ) : (
            <img className={styles.image} src={imageSrc} alt={spaceName} />
          )}
          <div className={styles.detailGrid}>
            <div className={styles.detailBlock}>
              <Text className={styles.detailLabel}>{t('fields.roomName')}</Text>
              <Text className={styles.detailValue}>{spaceName}</Text>
            </div>
            <div className={styles.detailBlock}>
              <Text className={styles.detailLabel}>{t('fields.capacity')}</Text>
              <Text className={styles.detailValue}>{capacity ?? t('notProvided')}</Text>
            </div>
            <div className={styles.detailBlock}>
              <Text className={styles.detailLabel}>{t('fields.configuration')}</Text>
              <Text className={styles.detailValue}>{configuration ?? t('notProvided')}</Text>
            </div>
            <div className={styles.detailBlock}>
              <Text className={styles.detailLabel}>{t('fields.address')}</Text>
              <Text className={styles.detailValue}>{address}</Text>
            </div>
          </div>
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
};

export interface SelectedSpacesAccordionProps {
  spaces: ResolvedSelectedSpace[];
  building: Building | undefined;
  fallbackCapacity?: number;
  t: TranslateFn;
}

export const SelectedSpacesAccordion = ({
  spaces,
  building,
  fallbackCapacity,
  t,
}: SelectedSpacesAccordionProps) => {
  const styles = useSelectedSpacesAccordionStyles();

  return (
    <div className={styles.accordion} data-testid="selected-spaces-accordion">
      <Accordion collapsible multiple defaultOpenItems={[0]}>
        {spaces.map((space, index) => (
          <SpaceItem
            key={space.selection.spaceId || `space-${index}`}
            index={index}
            space={space}
            building={building}
            fallbackCapacity={fallbackCapacity}
            t={t}
          />
        ))}
      </Accordion>
    </div>
  );
};

SelectedSpacesAccordion.displayName = 'SelectedSpacesAccordion';
